/**
 * UaePricingPicker — Canonical Plan Studio picker (2026-07-11).
 *
 * Reads /api/public/plan-catalog and lets the user pick a published +
 * public + sellable pricing offer. Emits canonical IDs
 * (plan_version_id + pricing_offer_id) upward. `pricing_matrix_id`,
 * plan-tier arrays, and legacy plan_keys are never submitted.
 */
import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Sparkles, FileBadge2, UserCog, Globe2 } from "lucide-react";
import { api } from "@/lib/api";

const BUNDLES = [
  { key: "certification",     label: "Certification ERP",   desc: "Leads · Quotations · Cases · Submissions · Renewals",   icon: FileBadge2 },
  { key: "hr",                label: "HR Payroll",          desc: "Employees · Payroll · Leave · Attendance · Reimbursements", icon: UserCog },
  { key: "certification_hr",  label: "Certification + HR",  desc: "Everything · Workspace switcher · Best value",           icon: Sparkles, popular: true },
];

export default function UaePricingPicker({ value, onChange }) {
  const [country, setCountry] = useState(null);
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/plan-catalog");
        const d = data?.data || {};
        setCountry(d.country || null);
        setPlans(Array.isArray(d.plans) ? d.plans : []);
      } finally { setLoading(false); }
    })();
  }, []);

  const bundle = value?.module_bundle    || "certification";
  const cycle  = value?.billing_cycle    || "monthly";
  const pickedOfferId = value?.pricing_offer_id || null;

  // Bundles that actually have offers.
  const availBundles = useMemo(() =>
    BUNDLES.filter(b => plans.some(p => p.module_bundle === b.key)),
    [plans]
  );
  // Plans for the current bundle + cycle.
  const bundlePlans = useMemo(() =>
    plans.filter(p => p.module_bundle === bundle && p.billing_cycle === cycle),
    [plans, bundle, cycle]
  );

  // Deterministic default / switch-preservation.
  //   1. keep the current pick if it applies to this bundle+cycle;
  //   2. else preserve the same plan_family (equivalent offer at the
  //      new cycle) whenever available;
  //   3. else pick the first offer in backend catalog order.
  // Marketing badges NEVER force selection.
  useEffect(() => {
    if (!country || bundlePlans.length === 0) return;
    const stillValid = bundlePlans.some(p => p.pricing_offer_id === pickedOfferId);
    if (stillValid) return;

    const prev = plans.find(p => p.pricing_offer_id === pickedOfferId);
    if (prev) {
      const eq = bundlePlans.find(p => p.plan_family_id === prev.plan_family_id);
      if (eq) { emitSelection(eq); return; }
    }
    emitSelection(bundlePlans[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, bundle, cycle, bundlePlans.length]);

  function emitSelection(plan) {
    if (!plan || !country) return;
    onChange?.({
      module_bundle: plan.module_bundle,
      billing_cycle: plan.billing_cycle,
      plan_version_id:  plan.plan_version_id,
      pricing_offer_id: plan.pricing_offer_id,
    }, {
      // Snapshot used by Register.jsx / backend for informational
      // metadata. Backend re-validates against the authoritative row.
      country_code:      country.country_code,
      currency_code:     plan.currency_code || country.currency_code,
      tax_name:          country.tax_name,
      tax_rate:          plan.tax_rate ?? country.tax_rate,
      module_bundle:     plan.module_bundle,
      billing_cycle:     plan.billing_cycle,
      subscription_value: plan.recurring_amount,
      setup_fee:         plan.setup_fee,
      plan_version_id:   plan.plan_version_id,
      pricing_offer_id:  plan.pricing_offer_id,
      plan_family_id:    plan.plan_family_id,
      plan_name:         plan.name,
      limits:            plan.limits || {},
      features:          plan.included_features || [],
    });
  }

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="uae-pricing-picker-loading">
        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse mb-3" />
        <div className="h-32 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }
  if (!country) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800" data-testid="uae-pricing-picker-empty">
        No enabled country configuration is available. Please contact support.
      </div>
    );
  }
  if (plans.length === 0) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800" data-testid="uae-pricing-picker-empty">
        No published plans are currently available for {country.country_name}.
      </div>
    );
  }

  const selected = bundlePlans.find(p => p.pricing_offer_id === pickedOfferId);
  const fmt = (n) => `${country.currency_code} ${Number(n || 0).toLocaleString()}`;
  const taxRate = selected?.tax_rate ?? country.tax_rate ?? 0;
  const vat = selected && !selected.tax_inclusive ? (selected.recurring_amount || 0) * (taxRate / 100) : 0;
  const total = selected ? (selected.recurring_amount || 0) + (selected.setup_fee || 0) + vat : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="uae-pricing-picker">
      {/* Header strip */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-teal-300 font-bold">
          <Globe2 className="w-3.5 h-3.5" /> Your subscription · {country.country_code}
        </div>
        <div className="mt-2 text-[11px] text-slate-300">
          {country.currency_code} · {country.tax_name} {country.tax_rate}%
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Bundle */}
        <Field label="Choose product">
          <div className="space-y-2" data-testid="uae-bundle-group">
            {availBundles.map((b) => {
              const active = bundle === b.key;
              const Icon = b.icon;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => onChange?.({
                    module_bundle: b.key, billing_cycle: cycle,
                    // Keep the same offer id — the auto-preserve effect
                    // will resolve the equivalent offer for the new bundle
                    // (same plan family) or fall back to the first offer.
                    plan_version_id: value?.plan_version_id || null,
                    pricing_offer_id: value?.pricing_offer_id || null,
                  })}
                  data-testid={`uae-bundle-${b.key}`}
                  className={`w-full text-left rounded-xl border p-3 transition ${active ? "border-teal-500 bg-teal-50/40 ring-1 ring-teal-500/30" : "border-slate-200 bg-white hover:border-slate-300"}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${active ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-600"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{b.label}</div>
                      <div className="text-[11px] text-slate-500 leading-snug">{b.desc}</div>
                    </div>
                    {b.popular ? <span className="ml-auto text-[9px] uppercase tracking-widest font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Best</span> : null}
                  </div>
                </button>
              );
            })}
          </div>
        </Field>

        {/* Cycle */}
        <Field label="Billing cycle">
          <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="uae-cycle-group">
            {["monthly", "annual"].map((c) => (
              <button key={c} type="button" data-testid={`uae-cycle-${c}`}
                onClick={() => onChange?.({
                  module_bundle: bundle, billing_cycle: c,
                  // Keep the same offer id — auto-preserve will find the
                  // equivalent offer at the new cycle (same plan family).
                  plan_version_id: value?.plan_version_id || null,
                  pricing_offer_id: value?.pricing_offer_id || null,
                })}
                className={`px-4 py-1 text-xs font-semibold rounded-full transition ${cycle === c ? "bg-slate-900 text-white shadow" : "text-slate-700"}`}>
                {c === "monthly" ? "Monthly" : "Annual"}
              </button>
            ))}
          </div>
        </Field>

        {/* Plan picker — one card per pricing offer for the chosen bundle+cycle */}
        <Field label="Choose plan">
          {bundlePlans.length === 0 ? (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="uae-plan-empty">
              No published plans for that product and billing cycle.
            </div>
          ) : (
            <div className="space-y-2" data-testid="uae-plan-group">
              {bundlePlans.map(p => {
                const sel = p.pricing_offer_id === pickedOfferId;
                return (
                  <button
                    key={p.pricing_offer_id}
                    type="button"
                    onClick={() => emitSelection(p)}
                    data-testid={`uae-plan-${p.pricing_offer_id}`}
                    data-selected={sel ? "true" : "false"}
                    aria-pressed={sel}
                    className={`w-full text-left rounded-xl border p-3 transition ${sel ? "border-teal-500 bg-teal-50/40 ring-1 ring-teal-500/30" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                        {p.description ? (
                          <div className="text-[11px] text-slate-500 leading-snug">{p.description}</div>
                        ) : null}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-slate-900">{fmt(p.recurring_amount)}</div>
                        <div className="text-[10px] text-slate-500">per {p.billing_cycle === "annual" ? "year" : "month"}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Field>

        {/* Summary */}
        {selected ? (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="uae-price-summary">
            <div className="grid grid-cols-2 gap-y-1.5 text-sm text-slate-700">
              <div>Subscription <span className="text-[10px] text-slate-400">({cycle === "annual" ? "per year" : "per month"})</span></div>
              <div className="text-right font-mono">{fmt(selected.recurring_amount)}</div>
              {selected.setup_fee > 0 ? (
                <>
                  <div>Setup fee <span className="text-[10px] text-slate-400">(one-time)</span></div>
                  <div className="text-right font-mono">{fmt(selected.setup_fee)}</div>
                </>
              ) : null}
              <div>{country.tax_name} ({taxRate}%)</div>
              <div className="text-right font-mono">{fmt(vat)}</div>
              <div className="col-span-2 border-t border-slate-300 my-1" />
              <div className="font-bold text-slate-900">Total today</div>
              <div className="text-right font-bold text-slate-900 font-mono" data-testid="uae-price-total">{fmt(total)}</div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">{label}</label>
      {children}
    </div>
  );
}
